import type { ReactNode, MouseEvent } from 'react';

export interface SidebarNavItem {
  /** Stable key, matched against `activeKey` (e.g. the route segment). */
  key: string;
  label: string;
  /** Icon node for the 18px icon slot (monochrome — inherits color). */
  icon?: ReactNode;
  /** Count pill (tasks, projects, …). Omit to hide. */
  badge?: number | string;
  /** Target path. Rendered on the anchor for middle-click/copy-link. */
  href?: string;
}

export interface SidebarNavGroup {
  /** Uppercase group label (e.g. "עבודה יומית"). Omit for an unlabeled group. */
  title?: string;
  items: SidebarNavItem[];
}

export interface SidebarProps {
  /** App name in the header row (e.g. "אלקה מנג׳ר", "אלקה CRM"). */
  appName: string;
  /** Tagline under the app name (e.g. "חשמלאות תעשייתית", "צוות מכירות"). */
  tagline?: string;
  groups: SidebarNavGroup[];
  /** Key of the active nav item (derive from the current route). */
  activeKey?: string;
  /**
   * Navigation handler — call your router here (e.g. `navigate(item.href)`).
   * When provided, anchor default navigation is prevented.
   */
  onNavigate?: (item: SidebarNavItem) => void;
  /** Sidebar width in px: 240 (elka-manager) or 200 (crm). Widths ≤200 render compact header type. */
  width?: 200 | 240;
  /** Footer slot — user row (avatar + name/role + settings affordance). */
  footer?: ReactNode;
  className?: string;
}

/**
 * Calm-v2 fixed right-side (RTL) sidebar: surface bg, 1px line edge,
 * grouped nav with 10px uppercase group labels, brandSoft active item with
 * a 3px brand bar on the right edge, count pills. Config-driven and
 * router-agnostic. Only valid in apps that import `@elka/theme` v2/calm.css.
 */
export function Sidebar({
  appName,
  tagline,
  groups,
  activeKey,
  onNavigate,
  width = 240,
  footer,
  className,
}: SidebarProps) {
  const compact = width <= 200;

  const handleClick = (item: SidebarNavItem) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(item);
    }
  };

  return (
    <aside
      className={`bg-surface border-line sticky top-0 flex h-screen shrink-0 flex-col border-l ${className ?? ''}`}
      style={{ width }}
    >
      {/* Header: brand square + app name + tagline */}
      <div className="border-line flex items-center gap-2.5 border-b px-[18px] pt-[18px] pb-3.5">
        <div
          className={`bg-brand flex shrink-0 items-center justify-center rounded-[9px] font-black text-white ${
            compact ? 'h-7 w-7 text-[13px]' : 'h-[34px] w-[34px] text-base'
          }`}
        >
          א
        </div>
        <div className="min-w-0">
          <div
            className={`text-ink truncate font-extrabold tracking-[-0.2px] ${
              compact ? 'text-[13px]' : 'text-sm'
            }`}
          >
            {appName}
          </div>
          {tagline && (
            <div className={`text-muted truncate ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
              {tagline}
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-2.5">
        {groups.map((group, gi) => (
          <div key={group.title ?? gi} className="mb-2.5">
            {group.title && (
              <div className="text-sub px-2.5 pt-2 pb-1 text-[10px] font-bold tracking-[0.8px] uppercase">
                {group.title}
              </div>
            )}
            {group.items.map((item) => {
              const isActive = item.key === activeKey;
              return (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={handleClick(item)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`rounded-control relative mb-px flex cursor-pointer items-center gap-2.5 px-2.5 py-2 text-[13px] transition-colors duration-150 ${
                    isActive
                      ? 'bg-brand-soft text-brand font-bold'
                      : 'text-ink-2 hover:bg-surface-soft font-medium'
                  }`}
                >
                  {item.icon && (
                    <span
                      className={`flex w-[18px] shrink-0 items-center justify-center ${
                        isActive ? 'text-brand' : 'text-muted'
                      }`}
                    >
                      {item.icon}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.badge != null && (
                    <span
                      className={`shrink-0 rounded-full px-[7px] py-px text-[10px] font-bold tabular-nums ${
                        isActive ? 'bg-brand text-white' : 'bg-line text-muted'
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                  {isActive && (
                    <span className="bg-brand absolute top-2 -right-2.5 bottom-2 w-[3px] rounded-sm" />
                  )}
                </a>
              );
            })}
          </div>
        ))}
      </nav>

      {footer && (
        <div className="border-line flex items-center gap-2.5 border-t px-3.5 py-3">{footer}</div>
      )}
    </aside>
  );
}
